import { useRouter } from "next/router";
import { useOrganization } from "~hooks/useOrganization";
import { setState } from "~features/seated-reservation/store";
import { CreateOrderInput, OrderOriginatedFrom } from "~graphql/sdk";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import { handlePromise, sdk } from "~lib";
import { getGraphQLError, showToast, trackCheckout } from "~lib/helpers";
import {
  buildCustomErrorMessage,
  buildCustomErrorTitle,
  CustomErrorResult,
} from "./createOrderErrorHelpers";
import { seekaTrackCheckout } from "~lib/seeka";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import { useAtom } from "jotai";
import { isPrechargeFlowAtom } from "~features/nonseated-reservation/isPrechargeFlowAtom";
import { getRegistrationFormKeyAndValues } from "~features/events/components/RegistrationModalContent";
import { CreateOrderResultOrder } from "./types";
import { addQueryParams, removeQueryParams } from "~lib/helpers/queryParams";
import { useCheckoutVariant } from "~features/checkout/common/hooks/useCheckoutVariant";

const MAX_ORDER_CREATION_TIME_MS = 1000 * 60 * 100; // 100 minutes
const ORDER_CREATION_POLLING_INTERVAL_MS = 1000 * 2; // 2 seconds

export type ErrorMessage = CustomErrorResult | string | null;

export type CreateOrderOptions = {
  skipRedirectToCheckout?: boolean;
  isExpressCheckout?: boolean;
  paymentType?: string;
};

export const useCreateOrder = () => {
  const router = useRouter();
  const { organization, hasFeature } = useOrganization();
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const converge = useSeekaConverge();
  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);
  const checkoutVariant = useCheckoutVariant();

  const createOrder = async (
    sdkFn: typeof sdk,
    orgId: string,
    input: CreateOrderInput
  ) => handlePromise(async () => sdkFn({ orgId }).createOrder({ input }));

  const getOrderJobStatus = async (
    sdkFn: typeof sdk,
    orgId: string,
    jobId: string
  ) => handlePromise(async () => sdkFn({ orgId }).getOrderJobStatus({ jobId }));

  const createOrderAndWaitForCompletion = async (
    input: CreateOrderInput,
    onError?: (
      errorMessage: ErrorMessage,
      errorTitle?: string
    ) => Promise<void> | void,
    onSuccess?: (order: CreateOrderResultOrder) => Promise<void> | void,
    options: CreateOrderOptions = {}
  ) => {
    const registrationFormKeyAndValues = getRegistrationFormKeyAndValues(
      input.releaseId
    );

    const [, registrationFormValues] = registrationFormKeyAndValues || [];

    const smsDefaultDisabled =
      hasFeature("OrderSmsConfirmationDisabled") ||
      hasFeature("OrderSmsConfirmationDefaultOptOut");

    const sendSmsConfirmation =
      options.isExpressCheckout && !smsDefaultDisabled;

    const cartRecovery = router.query.cartRecovery === "true";

    const singlePageCheckoutEnabled = hasFeature("SinglePageCheckout");

    const { error: createError, data: createOrderResponse } =
      await handlePromise(
        async () =>
          await createOrder(
            await recaptchaSdkGenerator("createOrder"),
            organization?.id,
            {
              ...input,
              ...(router.query.customer && {
                userId: router.query.customer as string,
              }),
              ...(router.query.posId && {
                posId: router.query.posId as string,
              }),
              ...(cartRecovery && {
                isCreatedFromRecoveredCart: cartRecovery,
              }),
              ...(isPrechargeFlow && {
                buyerInformation: {
                  email: registrationFormValues.email,
                  firstName: registrationFormValues.firstName,
                  lastName: registrationFormValues.lastName,
                  phoneNumber: registrationFormValues.phoneNumber,
                },
                usableForMarketing: registrationFormValues.usableForMarketing,
              }),
              sendSmsConfirmation,
              orderOriginatedFrom: options.isExpressCheckout
                ? OrderOriginatedFrom.ExpressCheckout
                : singlePageCheckoutEnabled && checkoutVariant === "spc"
                  ? OrderOriginatedFrom.StandardCheckout
                  : null,
              seekaPId: converge?.identity?.personId ?? undefined,
            }
          )
      );
    let order = createOrderResponse?.data?.createOrder?.order;
    const jobId = createOrderResponse?.data?.createOrder?.jobId;
    let customError = createOrderResponse?.data?.createOrder?.createOrderError;
    let graphQlError = createOrderResponse?.error ?? createError;
    let clientError: Error = undefined;
    if (!order && !graphQlError && !customError && jobId) {
      const startTime = new Date().getTime();
      do {
        const {
          error: getOrderJobStatusError,
          data: getOrderJobStatusResponse,
        } = await handlePromise(
          async () =>
            await getOrderJobStatus(
              await recaptchaSdkGenerator("getOrderJobStatus"),
              organization?.id,
              jobId
            )
        );
        graphQlError =
          getOrderJobStatusResponse?.error ?? getOrderJobStatusError;
        order = getOrderJobStatusResponse?.data?.getOrderJobStatus?.order;
        customError =
          getOrderJobStatusResponse?.data?.getOrderJobStatus?.createOrderError;

        if (new Date().getTime() > startTime + MAX_ORDER_CREATION_TIME_MS) {
          clientError = new Error(
            "There was an issue with your network. Please reconnect and try again."
          );
        }
        await new Promise((resolve) =>
          setTimeout(resolve, ORDER_CREATION_POLLING_INTERVAL_MS)
        );
      } while (!order && !graphQlError && !clientError && !customError);
    }

    if (!order && !graphQlError && !customError && !clientError) {
      clientError = new Error("Order creation failed. Please try again.");
    }

    let errorMessage = (() => {
      if (graphQlError) {
        return getGraphQLError(graphQlError)?.message;
      }
      if (customError) {
        return buildCustomErrorMessage(customError);
      }
      return clientError?.message;
    })();

    let errorTitle: string | undefined = undefined;

    if (customError) {
      errorTitle = buildCustomErrorTitle(customError);
    }

    const errorCode = graphQlError && getGraphQLError(graphQlError)?.statusCode;
    // 987 is the error code for unmet purchase restrictions
    if (errorCode === 987) {
      const message =
        typeof errorMessage === "object" ? errorMessage.html : errorMessage;

      errorTitle = "This ticket has purchase restrictions";
      errorMessage = {
        html: message,
        plainText: message,
      };

      setState({
        unmetPurchaseRestrictionsMessage: message,
      });
    }

    if (errorMessage) {
      if (onError) {
        await onError(errorMessage, errorTitle);
      } else {
        showToast(errorMessage, "error");
      }

      // Clean up the modal query params from express ceckout flow
      if (options.isExpressCheckout) {
        removeQueryParams(router, ["orderId", "paymentType", "modal"]);
      }

      return false;
    }

    trackCheckout(order, {
      currency: organization.currency,
    });

    seekaTrackCheckout(converge, order, organization.currency);
    if (onSuccess) {
      await onSuccess(order);
    }

    // Adding the order to the query params will allow the express checkout modal to proceed with the payment
    if (options.isExpressCheckout) {
      addQueryParams(router, { orderId: order.id });
    }

    sessionStorage.removeItem("accessCodeId");

    const redirectToCheckout =
      !options.skipRedirectToCheckout && !options.isExpressCheckout;

    if (redirectToCheckout) {
      await router.push("/checkout/[orderId]", `/checkout/${order.id}`);
    }

    return true;
  };

  return { createOrderAndWaitForCompletion };
};
